import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from "rxjs";
import { FilterComponent, type IFilter } from '../../oex-ui-kit/components/filter/filter.component';
import { type IPagingData } from '../../oex-ui-kit/components/grid/grid';
import { DEFAULT_PAGE_SIZE } from '../../oex-ui-kit/components/grid/grid.constants';
import type { IHeaderNotification } from "../../oex-ui-kit/components/notification-popup/notification-popup.component";
import type { IUploadedFileInfo } from '../../oex-ui-kit/components/upload-input/upload-input.component';
import type {
  DictionaryKey,
  IAddBadgeRequest,
  IAdjustPointsRequest,
  IAdminMemberDesc,
  IAdminMemberDetails,
  IAdminNewsletter,
  IAdminPermission,
  IAdminPermissionResponse,
  IAdminReward,
  IAdminRewardListItem,
  IAdminRewardOrderListItem,
  IAdminRolesResponse,
  IAdminUser,
  IAdminUsersResponse,
  IApiEventItem,
  IBadgeDesc,
  IBillingLogItem,
  IChallenge,
  IChallengeActivityItem,
  IChallengeActivityResponse,
  IChallengeListItem,
  IChallengesApprovalItem,
  IChallengesApprovalStatusChange,
  ICompletionStageData,
  ICustomField,
  IDictionaryResponse,
  IEvent,
  IEventLogItem,
  IGMDictionaryItem,
  IHomePageChallengeList,
  IIdResponse,
  ILeaderboardData,
  ILevel,
  ILevelResponse,
  ILogActivityRequest,
  IMemberEmailDetails,
  IMemberEmailLogItem,
  IMemberLevelResponse, IMemberOrder,
  IMemberOverview,
  IMemberReward,
  IMemberShipping,
  IOAuthUlrRequestParams,
  IOAuthUrlResponse,
  IPagePreview,
  IQuestionType,
  IRefreshPointsResponse,
  IRewardsOrdersStatusChange,
  IRule,
  ISchemaResponse,
  IStageType,
  IState,
  IStatusResponse,
  ITargeting,
  ITargetingCondition,
  ITargetingGroupListItem,
  ITargetingMember,
  ITargetingSaveResponse,
  ITimezone,
  IUserChallenge,
  IUserProfile,
  IUserStatisticsResponse,
  IUserStatsResponse,
  WelcomeStatus,
  IAdminSidebarCounters,
  IUserNotificationPreferences
} from "./api.types";

const API_URL = '/api/';

@Injectable({
  providedIn: 'root'
})
export class APIService {
  private http = inject(HttpClient);
  readonly socialShareUploadUrl = this.url + 'upload/social-share';

  get url() {
    return API_URL;
  }

  get bannerUploadUrl() {
    return this.url + 'upload/banner';
  }

  get userAvaUploadUrl() {
    return this.url + 'upload/avatar';
  }

  get schemaUploadUrl() {
    return this.url + 'upload/member';
  }

  get userBadgeUploadUrl() {
    return this.url + 'upload/badge';
  }

  get rewardUploadUrl() {
    return this.url + 'upload/reward';
  }

  stageUploadUrl(shortName: string) {
    return this.url + `upload/${shortName}`;
  }


  async getOAuthUrl(params: IOAuthUlrRequestParams) {
    return firstValueFrom(
      this.http.post<IOAuthUrlResponse>(this.url + 'oauth/url', params)
    );
  }

  async getState() {
    return firstValueFrom(this.http.get<IState>(this.url + 'state', {withCredentials: true})
    );
  }

  doLogout() {
    return firstValueFrom(
      this.http.get(this.url + 'oauth/logout')
    )
  }

  getUserAvatarUrl(fileInfo?: IUploadedFileInfo) {
    if (!fileInfo?.id) {
      return '';
    }
    return this.url + `files/user/${fileInfo.id}`;
  }

  async getDictionary(key: DictionaryKey[] | string, term = '') {
    const keys = Array.isArray(key) ? key : [key];
    return firstValueFrom(
      this.http.get<IDictionaryResponse>(this.url + `dictionary/${keys.join(',')}?search=${term}`)
    )
  }

  async getCountryStates(countryId: number) {
    return firstValueFrom(
      this.http.get<IGMDictionaryItem[]>(this.url + `dictionary/country/state?country_id=${countryId}`)
    )
  }

  async getAllDictionaries() {
    return firstValueFrom(
      this.http.get<IDictionaryResponse>(this.url + `dictionary`)
    )
  }

  async getTimezones() {
    return firstValueFrom(
      this.http.get<ITimezone[]>(this.url + `admin/timezones`)
    )
  }

  /*async deleteAdminUser(userId: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/user/${userId}`)
    );
  }*/

  async getAdminRoles() {
    return firstValueFrom(
      this.http.get<IAdminRolesResponse>(this.url + `admin/role`)
    )
  }

  async getAdminPermissionsList(sorting: string = '') {
    if (!sorting) {
      sorting = 'n.asc';
    }
    return firstValueFrom(
      this.http.get<IAdminPermissionResponse>(this.url + `admin/permission?sorting=${sorting}`)
    )
  }

  async saveAdminPermissions(perms: IAdminPermission[]) {
    return firstValueFrom(
      this.http.post<void>(this.url + `admin/permission`, {items: perms})
    );
  }

  async deleteAdminPermission(idPerm: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/permission/${idPerm}`)
    );
  }

  async getAdminUsersList(sorting: string = '', roles?: string[]) {
    if (!sorting) {
      sorting = 'n.asc';
    }
    return firstValueFrom(
      this.http.get<IAdminUsersResponse>(this.url + `admin/user?sorting=${sorting}&roles=${(roles || []).join(',')}`)
    )
  }

  async deleteAdminUser(userId: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/user/${userId}`)
    );
  }

  async saveAdminUsers(users: IAdminUser[]) {
    return firstValueFrom(
      this.http.post<IAdminUser[]>(this.url + `admin/user`, {items: users})
    );
  }

  async getChallengeDetails(id: number) {
    return firstValueFrom(
      this.http.get<IChallenge>(this.url + `challenge/get/${id}`)
    );
  }

  async saveChallenge(challenge: IChallenge) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'challenge/details', challenge)
    );
  }

  async getChallenges() {
    return firstValueFrom(
      this.http.get<IPagingData<IChallenge>>(this.url + 'challenge')
    );
  }

  async getChallengeStageTypes() {
    return firstValueFrom(
      this.http.get<IStageType[]>(this.url + 'challenge/stage-types')
    );
  }

  async publishChallenge(challengeId: number/*, challengeStartDate: string, challengeEndDate: string*/) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'challenge/publish', {id: challengeId/*, challengeStartDate, challengeEndDate */})
    );
  }

  async unpublishChallenge(challengeId: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'challenge/unpublish', {id: challengeId})
    );
  }

  async archiveChallenge(challengeId: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'challenge/archive', {id: challengeId})
    );
  }

  async getChallengeRules(inChallenge = true) {
    return firstValueFrom(
      this.http.get<IRule[]>(this.url + 'group/rules?in_challenge=' + (inChallenge ? '1' : '0'))
    );
  }


  async getTopMembers(condition: ITargetingCondition) {
    return firstValueFrom(
      this.http.post<IPagingData<IGMDictionaryItem>>(this.url + 'group/calc_draft', {condition})
    );
  }

  async getTargetingMembers(condition: ITargetingCondition, term = '', sorting = 'n.asc', currPage = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.post<IPagingData<ITargetingMember>>(this.url + `group/calc_draft/members?term=${encodeURIComponent(term)}&sorting=${sorting}&pageSize=${pageSize}&currPage=${currPage}`, {condition})
    );
  }

  async getMemberOverview() {
    return firstValueFrom(
      this.http.get<IMemberOverview>(this.url + 'member/overview')
    );
  }

  async getQuestionTypes() {
    return firstValueFrom(
      this.http.get<IQuestionType[]>(this.url + 'challenge/question-types')
    );
  }

  async getChallengeList(term = '', sorting: string, filter?: IFilter, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IChallengeListItem>>(this.url + `challenge?sorting=${sorting}&filter_term=${term}&pageSize=${pageSize}&currPage=${page}` + FilterComponent.getFilterParams(filter, 'filter_'))
    )
  }

  async getChallengeListForCurrentUser(sorting: string, filter?: IFilter, page = 1, pageSize = DEFAULT_PAGE_SIZE, term = '') {
    return firstValueFrom(
      this.http.get<IHomePageChallengeList>(this.url + `challenge/user?sorting=${sorting}&term=${encodeURIComponent(term)}&pageSize=${pageSize}&currPage=${page}` + FilterComponent.getFilterParams(filter, ''))
    )
  }

  async getMemberProfile() {
    return firstValueFrom(
      this.http.get<IUserProfile>(this.url + 'member/profile')
    );
  }

  async getMemberProfileSchema() {
    return firstValueFrom(
      this.http.get<ISchemaResponse>(this.url + 'member/profile/schema')
    );
  }

  async saveMemberProfile(data: IUserProfile) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + 'member/profile', data)
    );
  }

  async getTargetingGroupsList(term = '', sorting: string, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<ITargetingGroupListItem>>(this.url + `group?sorting=${sorting}&name=${term}&pageSize=${pageSize}&currPage=${page}`)
    )
  }

  async deleteTargetingGroup(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `group/${id}`)
    );
  }

  async getTargetingGroup(id: number) {
    return firstValueFrom(
      this.http.get<ITargeting>(this.url + `group/${id}`)
    );
  }

  async saveTargetingGroup(targeting: ITargeting) {
    return firstValueFrom(
      this.http.post<ITargetingSaveResponse>(this.url + 'group', targeting)
    );
  }

  async deleteTargetingGroupMember(groupId: number, memberId: number) {
    return firstValueFrom(
      this.http.delete<ITargeting>(this.url + `group/${groupId}/member/${memberId}`)
    );
  }

  async refreshPoints() {
    return firstValueFrom(
      this.http.get<IRefreshPointsResponse>(this.url + 'refresh_points')
    );
  }

  /*  async getNotifications() {
      return firstValueFrom(
        this.http.get<IHeaderNotification[]>(this.url + 'notifications')
      );
    }*/

  /*  async deleteUserFile(file: IUploadedFileInfo) {
      return firstValueFrom(
        this.http.delete(this.url + `files/avatar/${file.id}`)
      );
    }*/

  async getAdminLevels() {
    return firstValueFrom(
      this.http.get<ILevelResponse>(this.url + 'ap/level')
    );
  }

  async getAdminBadges(term = '', sorting: string, filter?: IFilter, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IBadgeDesc>>(this.url + `ap/badge?sorting=${sorting}&term=${term}&pageSize=${pageSize}&currPage=${page}` + FilterComponent.getFilterParams(filter))
    );
  }

  async getMemberLevels() {
    return firstValueFrom(
      this.http.get<IMemberLevelResponse>(this.url + 'level')
    );
  }

  async getAdminBadge(idBadge: number) {
    return firstValueFrom(
      this.http.get<IBadgeDesc>(this.url + `ap/badge/${idBadge}`)
    );
  }

  async getBadgeRules() {
    return firstValueFrom(
      this.http.get<IRule[]>(this.url + 'ap/badge/rules')
    );
  }

  async saveBadge(badge: IBadgeDesc) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + 'ap/badge', badge)
    );
  }

  async getAdminLevel(id: number) {
    return firstValueFrom(
      this.http.get<ILevel>(this.url + `ap/level/${id}`)
    );
  }

  async saveAdminLevel(level: ILevel) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + 'ap/level', level)
    );
  }

  async deleteAdminLevel(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `ap/level/${id}`)
    );
  }

  async deleteAdminMember(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `ap/member/${id}`)
    );
  }

  async getUserStatistics() {
    return firstValueFrom(
      this.http.get<IUserStatisticsResponse>(this.url + `user_indicators`)
    );
  }

  async getAdminMemberList(term = '', sorting: string, condition: ITargetingCondition, currPage = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.post<IPagingData<IAdminMemberDesc>>(this.url + `ap/member/search`, {
        sorting,
        term,
        pageSize,
        currPage,
        condition
      })
    )
  }

  async exportAdminMembersCSV(term = '', sorting: string, condition: ITargetingCondition) {
    return firstValueFrom(
      this.http.post(this.url + `ap/member/search/csv`, {sorting, term, condition}, {responseType: 'blob'})
    );
  }

  async getTagsForCurrentUser(categoryId?: number) {
    return firstValueFrom(
      this.http.get<IGMDictionaryItem[]>(this.url + `challenge/user/tags?category=${categoryId ?? ''}`)
    )
  }

  async getChallengeForCurrentUser(idChallenge: number, idCompletion?: number) {
    return firstValueFrom(
      this.http.get<IUserChallenge>(this.url + `challenge/user/get/${idChallenge}` + (idCompletion ? ('?cID=' + idCompletion) : ''))
    );
  }

  async completeStage(idStage: number, idCompletion: number|null, stageData?: ICompletionStageData) {
    return firstValueFrom(
      this.http.post<IUserChallenge>(this.url + `stage/completion`, {id: idStage, challenge_completion_id: idCompletion, stageData})
    );
  }

  async deleteCompletion(idChallenge: number) {
    return firstValueFrom(
      this.http.delete<IUserChallenge>(this.url + `challenge/${idChallenge}/completion`)
    );
  }

  async getPagePreview(url: string) {
    return firstValueFrom(
      this.http.get<IPagePreview>(this.url + `social/pagePreview`, { params: { url } })
    );
  }

  async getMemberDetails(idMember: number) {
    return firstValueFrom(
      this.http.get<IAdminMemberDetails>(this.url + `ap/member/${idMember}`)
    );
  }

  async getMemberEvents(idMember: number, eventTypes: number[] = [], page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IEvent>>(this.url + `ap/member/${idMember}/eventslog?pageSize=${pageSize}&currPage=${page}&event_type=` + eventTypes.join(','))
    );
  }

  async adminAdjustPoints(idMember: number, data: IAdjustPointsRequest) {
    return firstValueFrom(
      this.http.post<IUserStatsResponse>(this.url + `ap/member/${idMember}/adjust_points`, data)
    );
  }

  async adminAddBadge(idMember: number, data: IAddBadgeRequest) {
    return firstValueFrom(
      this.http.post<IUserStatsResponse>(this.url + `ap/member/${idMember}/add_badge`, data)
    );
  }

  async adminLogActivity(idMember: number, data: ILogActivityRequest) {
    return firstValueFrom(
      this.http.post<IUserStatsResponse>(this.url + `ap/member/${idMember}/log-activity`, data)
    );
  }

  async deleteBadge(idBadge: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `ap/badge/${idBadge}`)
    );
  }

  async getChallengeActivity(idChallenge: number, tab = 0, term = '', page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IChallengeActivityResponse>>(this.url + `challenge/activity/${idChallenge}?pageSize=${pageSize}&currPage=${page}&filter_tab=${tab}&filter_term=${term}`)
    );
  }

  async getMemberChallengeActivity(idMember: number, tab: number = 0, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IChallengeActivityItem>>(this.url + `ap/member/${idMember}/challenges?filter_tab=${tab}&pageSize=${pageSize}&currPage=${page}`)
    );
  }

  async getMemberEmails(idMember: number, name = '', status: string[] = [], sorting = '', page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    sorting = sorting || 'd.desc';
    return firstValueFrom(
      this.http.get<IPagingData<IMemberEmailLogItem>>(this.url + `ap/member/${idMember}/emails?pageSize=${pageSize}&currPage=${page}&sorting=${sorting}&name=${encodeURIComponent(name)}&status=${status.join(',')}`)
    );
  }

  async getMemberEmailDetails(idMember: number, deliveryId: number) {
    return firstValueFrom(
      this.http.get<IMemberEmailDetails>(this.url + `ap/member/${idMember}/emails/${deliveryId}`)
    );
  }

  async getAdminEventLog(filter?: IFilter, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IEventLogItem>>(this.url + `ap/eventslog?pageSize=${pageSize}&currPage=${page}` + FilterComponent.getFilterParams(filter, ''))
    );
  }

  async dismissChallenge(idChallenge: number, idCompletion: string | null = null) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `challenge/user/dismiss/${idChallenge}?cID=${idCompletion}`, {})
    );
  }

  async getAdminRewardsList(term = '', sorting: string, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    sorting = sorting || 'd.desc';
    return firstValueFrom(
      this.http.get<IPagingData<IAdminRewardListItem>>(this.url + `ap/reward?sorting=${sorting}&name=${term}&pageSize=${pageSize}&currPage=${page}`)
    );
  }

  async getAdminReward(idReward: number) {
    return firstValueFrom(
      this.http.get<IAdminReward>(this.url + `ap/reward/${idReward}`)
    );
  }

  async saveReward(reward: IAdminReward) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'ap/reward', reward)
    );
  }

  async publishReward(idReward: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/reward/${idReward}/publish`, {})
    );
  }

  async unpublishReward(idReward: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/reward/${idReward}/unpublish`, {})
    );
  }

  async archiveReward(idReward: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/reward/${idReward}/archive`, {})
    );
  }

  async lockMember(idMember: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/member/${idMember}/block`, {})
    );
  }

  async unlockMember(idMember: number) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/member/${idMember}/unblock`, {})
    );
  }

  async getMemberRewards(sorting = '', page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    if (!sorting) {
      sorting = 'p.desc';
    }
    return firstValueFrom(
      this.http.get<IPagingData<IMemberReward>>(this.url + `reward?sorting=${sorting}&pageSize=${pageSize}&currPage=${page}`)
    );
  }

  getAdminRewardsOrderList(search: string, sorting: string, filter?: IFilter, page: number = 1) {
    return firstValueFrom(
      this.http.get<IPagingData<IAdminRewardOrderListItem>>(this.url + `ap/redemption?sorting=${sorting}&name=${search}&pageSize=${DEFAULT_PAGE_SIZE}&currPage=${page}` + FilterComponent.getFilterParams(filter, ''))
    );
  }

  // Challenge names available in the corporate approval queue, narrowed by the approval status
  async getApprovalChallenges(statusId?: number | string) {
    return firstValueFrom(
      this.http.get<IGMDictionaryItem[]>(this.url + `dictionary/challenge/approval?filter_status=${encodeURIComponent(statusId ?? '')}`)
    );
  }

  async getChallengesApprovalList(search: string, sorting: string, filter?: IFilter, page = 1) {
    return firstValueFrom(
      this.http.get<IPagingData<IChallengesApprovalItem>>(this.url + `challenge/approval/list?sorting=${sorting}&filter_term=${search}&pageSize=${DEFAULT_PAGE_SIZE}&currPage=${page}` + FilterComponent.getFilterParams(filter, 'filter_'))
    );
  }

  async saveChallengesApprovalStatus(changes: IChallengesApprovalStatusChange[]) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'challenge/approval/save', changes)
    );
  }

  async saveAdminMemberProfile(data: IAdminMemberDetails) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/member/${data.id}/profile`, data)
    );
  }

  async getAdminMemberShipping(idMember: number) {
    return firstValueFrom(
      this.http.get<IMemberShipping>(this.url + `ap/member/${idMember}/profile/shipping`)
    );
  }

  async saveAdminMemberProfileShipping(idMember: number, data: IMemberShipping) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + `ap/member/${idMember}/profile/shipping`, data)
    );
  }

  async getMemberShipping() {
    return firstValueFrom(
      this.http.get<IMemberShipping>(this.url + `member/profile/shipping`)
    );
  }

  async saveAdminMemberShipping(idMember: number, data: IMemberShipping) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + `ap/member/${idMember}/shipping`, data)
    );
  }

  async saveMemberShipping(shipping: IMemberShipping) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + `member/profile/shipping`, shipping)
    );
  }

  async redeemReward(idReward: number, shipping?: IMemberShipping, member_comment = '') {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + `reward/order`, {
        reward: {id: idReward}, shipping, member_comment
      })
    );
  }

  async getMemberOrders(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IMemberOrder>>(this.url + `member/profile/orders?&pageSize=${pageSize}&currPage=${page}`)
    );
  }

  async getAdminCustomFieldsList(onlyUnused = false) {
    return firstValueFrom(
      this.http.get<ICustomField[]>(this.url + `ap/additionalfields?unused=${onlyUnused}`)
    );
  }

  async saveAdminCustomField(field: ICustomField) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/additionalfields`, field)
    );
  }

  async unDismissChallenge(idCompletion: number) {
    return firstValueFrom(
      this.http.post<{challenge_completion_id: number}>(this.url + `challenge/user/undismiss/${idCompletion}`, {})
    );
  }

  async getNotifications() {
    return firstValueFrom(
      this.http.get<IHeaderNotification[]>(this.url + `notifications/user`)
    )
  }

  async getMemberNotificationPreferences(idMember: number) {
    return firstValueFrom(
      this.http.get<IUserNotificationPreferences>(this.url + `ap/member/${idMember}/notification_preferences`)
    );
  }

  async saveMemberNotificationPreferences(idMember: number, preferences: IUserNotificationPreferences) {
    return firstValueFrom(
      this.http.post<void>(this.url + `ap/member/${idMember}/notification_preferences`, preferences)
    );
  }

  async markNotificationsAsRead(ids: number[]) {
    return firstValueFrom(
      this.http.post<void>(this.url + `notification/view`, {ids})
    )
  }

  async saveRewardOrdersStatus(changes: IRewardsOrdersStatusChange[]) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + `ap/redemption`, changes)
    );
  }

  getExportChallengeLink(challengeId: number) {
    return this.url + `challenge/activity/${challengeId}/report`;
  }

  getExportEventsLink(filter?: IFilter) {
    return this.url + `ap/eventslog/csv?` + FilterComponent.getFilterParams(filter, '');
  }

  async unblockMember(message: string) {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'member/unblock_message', { message })
    );
  }

  async accountDeletionRequest() {
    return firstValueFrom(
      this.http.post<IStatusResponse>(this.url + 'member/deletion_request', {})
    );
  }

  async getAdminNewsletter(term = '', page = 1, sorting = 'n.asc', pageSize = 30) {
    if (!sorting) {
      sorting = 'n.asc';
    }
    const params = `?&term=${term}&pageSize=${pageSize}&currPage=${page}&sorting=${sorting}`;

    return firstValueFrom(
      this.http.get<IPagingData<IAdminNewsletter>>(this.url + `admin/newsletter` + params)
    )
  }

  saveAdminNewsletter(newsletter: IAdminNewsletter) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + `admin/newsletter`, newsletter)
    );
  }

  deleteAdminNewsletter(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/newsletter/${id}`)
    );
  }

  archiveAdminNewsletter(id: number) {
    return firstValueFrom(
      this.http.post<void>(this.url + `admin/newsletter/${id}/archive`, {})
    );
  }

  unarchiveAdminNewsletter(id: number) {
    return firstValueFrom(
      this.http.post<void>(this.url + `admin/newsletter/${id}/unarchive`, {})
    );
  }

  async sendTestNewsletter(slug: string) {
    return firstValueFrom(
      this.http.get<void>(this.url + `admin/newsletter/${slug}/test`)
    )
  }

  async getActivationChallenge(full = false) {
    return firstValueFrom(
      this.http.get<IChallengeListItem>(this.url + 'challenge/activation?full=' + (full ? '1' : '0'))
    );
  }

  // Long polling for welcome status
  async getWelcomeStatus() {
    return firstValueFrom(
      this.http.get<{welcome_status: WelcomeStatus}>(this.url + 'welcome_status')
    );
  }

  async claimDailyReward() {
    return firstValueFrom(
      this.http.post<IState>(this.url + 'daily_reward/claim', {})
    );
  }

  async getAdminApiEvents(term: string, source: number, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    const strSource = source === undefined ? '' : source.toString();

    return firstValueFrom(
      this.http.get<IPagingData<IApiEventItem>>(this.url + `ap/event?name=${term}&source=${strSource}&pageSize=${pageSize}&currPage=${page}`)
    );
  }

  async getAdminApiEvent(id: number) {
    return firstValueFrom(
      this.http.get<IApiEventItem>(this.url + `ap/event/${id}`)
    );
  }

  async saveAdminApiEvent(event: IApiEventItem) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + 'ap/event', event)
    );
  }

  async deleteAdminApiEvent(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `ap/event/${id}`)
    );
  }

  async getPointsLog(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    return firstValueFrom(
      this.http.get<IPagingData<IBillingLogItem>>(this.url + `member/billing_history?pageSize=${pageSize}&currPage=${page}`)
    );
  }

  async duplicateReward(idReward: number) {
    return firstValueFrom(
      this.http.post<IAdminReward>(this.url + `ap/reward/${idReward}/copy`, {})
    );
  }

  async duplicateChallenge(idChallenge: number) {
    return firstValueFrom(
      this.http.post<IChallenge>(this.url + `challenge/${idChallenge}/copy`, {})
    );
  }

  // /member - leaderboard
  async getLeaderboard(filter: 'global' | 'you' | 'your-country', sorting = '', page = 1, pageSize = DEFAULT_PAGE_SIZE) {
    const additionalParams = filter === 'global' ? '' : (filter === 'you' ? '&you=1' : '&country=1');
    return firstValueFrom(
      this.http.get<ILeaderboardData>(this.url + `member?&sorting=${sorting}&pageSize=${pageSize}&currPage=${page}` + (additionalParams ? ('&' + additionalParams) : ''))
    );
  }

  async getAdminCounters() {
    return firstValueFrom(
      this.http.get<IAdminSidebarCounters>(this.url + 'admin/counters')
    );
  }

  // /member/notification_preferences
  async getNotificationPreferences() {
    return firstValueFrom(
      this.http.get<IUserNotificationPreferences>(this.url + `member/notification_preferences`)
    );
  }

  async saveNotificationPreferences(preferences: IUserNotificationPreferences) {
    return firstValueFrom(
      this.http.post<void>(this.url + `member/notification_preferences`, preferences)
    );
  }
}
